
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { handleApiError } from '@/core/helpers/handleApiError'
import { FormField, FormValues } from './formField'

/**
 * Универсальная форма в модальном окне для действий администратора:
 * причина ручной выдачи, возврат, подтверждение оплаты, выставление счёта, товары и т.д.
 * Поля описываются конфигурацией; в запрос попадают только показанные и непустые поля.
 * Ошибки сервера (422) показываются под полями, остальные ошибки через handleApiError.
 */
export default defineComponent({
  name: 'PaymentFormDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, required: true },
    submitText: { type: String, default: 'Сохранить' },
    fields: { type: Array as PropType<FormField[]>, required: true },
    initial: { type: Object as PropType<FormValues>, default: () => ({}) },
    /** Действие: получает значения формы, возвращает промис. */
    action: {
      type: Function as PropType<(payload: Record<string, any>) => Promise<unknown>>,
      required: true,
    },
  },
  emits: ['update:modelValue', 'done'],
  setup(props, { emit }) {
    const values = reactive<FormValues>({})
    const errors = reactive<Record<string, string>>({})
    const loading = ref(false)

    const visible = computed({
      get: () => props.modelValue,
      set: (value: boolean) => emit('update:modelValue', value),
    })

    const shownFields = computed(() =>
      props.fields.filter(field => !field.show || field.show(values))
    )

    const isRequired = (field: FormField): boolean =>
      typeof field.required === 'function'
        ? field.required(values)
        : Boolean(field.required)

    const isEmpty = (value: unknown): boolean =>
      value === '' ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)

    const clearErrors = () => {
      Object.keys(errors).forEach(key => delete errors[key])
    }

    // При каждом открытии форма начинается заново.
    watch(
      () => props.modelValue,
      opened => {
        if (!opened) {
          return
        }
        Object.keys(values).forEach(key => delete values[key])
        props.fields.forEach(field => {
          values[field.key] =
            props.initial[field.key] !== undefined
              ? props.initial[field.key]
              : field.type === 'switch'
              ? false
              : field.multiple
              ? []
              : undefined
        })
        clearErrors()
      }
    )

    const validate = (): boolean => {
      clearErrors()
      shownFields.value.forEach(field => {
        const value = values[field.key]
        if (isRequired(field) && isEmpty(value)) {
          errors[field.key] = 'Обязательное поле'
        } else if (
          field.minLength &&
          typeof value === 'string' &&
          value.trim().length < field.minLength
        ) {
          errors[field.key] = `Минимум ${field.minLength} символа`
        }
      })
      return Object.keys(errors).length === 0
    }

    const buildPayload = (): Record<string, any> => {
      const payload: Record<string, any> = {}
      shownFields.value.forEach(field => {
        const value = values[field.key]
        if (!isEmpty(value) || typeof value === 'boolean') {
          payload[field.key] = typeof value === 'string' ? value.trim() : value
        }
      })
      return payload
    }

    /** Раскладывает 422 по полям; что не нашло поле, показывает сообщением. */
    const showServerErrors = (error: any) => {
      const serverErrors =
        error && error.response && error.response.data && error.response.data.errors
      if (!serverErrors) {
        handleApiError(error)
        return
      }
      Object.keys(serverErrors).forEach(serverKey => {
        const message = String([].concat(serverErrors[serverKey])[0])
        const field = props.fields.find(
          item => (item.errorKey || item.key) === serverKey
        )
        if (field) {
          errors[field.key] = message
        } else {
          ElMessage({ type: 'warning', message })
        }
      })
    }

    const submit = async () => {
      if (!validate()) {
        return
      }
      loading.value = true
      try {
        const result = await props.action(buildPayload())
        visible.value = false
        emit('done', result)
      } catch (error) {
        showServerErrors(error)
      } finally {
        loading.value = false
      }
    }

    return { values, errors, loading, visible, shownFields, isRequired, submit }
  },
})
